import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Divider,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Image,
  Stack,
  StackDivider,
  Text,
  Wrap,
  useDisclosure,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { TabLink, TabLinkList } from '@/components/TabLink';
import { useOrganizationPermissions } from '@/hooks/useOrganizationPermissions';
import { usePermissions } from '@/hooks/usePermissions';
import { Card } from '@/imports/ui/chakra/components/Card';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { EditIcon, MessageCircleIcon } from '@/imports/ui/chakra/feather';
import { NoAccessComponent } from '@/imports/ui/components/NoAccess';
import { RequestDemoButton } from '@/imports/ui/components/RequestDemoButton';
import { useAutoFollowSponsor } from '@/imports/ui/hooks/useAutoFollowSponsor';
import { Butterfly, ButterflyMain } from '@/layouts/Butterfly';
import { tokenStorage } from '@/modules/accounts/tokenStorage';
import { showChatIconInMobile } from '@/utilities';

import {
  organizationPageLayout_OrganizationQuery,
  organizationPageLayout_OrganizationQueryVariables,
} from './graphql-types/organizationPageLayout_OrganizationQuery';

import { InfoCard } from '../../InfoCard';
import { OrganizationProfileImage } from '../../OrganizationProfileImage';
import { SponsorPagePromotionCard } from '../components/SponsorPagePromotionCard';
import { OrganizationFollowButton } from './components/FollowButton';
import { MessageModal } from './components/MessageModal';
import { TierCard } from './components/TierCard';

export const ORGANIZATION_QUERY = gql`
  query organizationPageLayout_OrganizationQuery(
    $input: SingleOrganizationInput!
  ) {
    organization(input: $input) {
      result {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
        isSponsor
        displayName
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        numFollowers
        sponsorship {
          currentTier {
            name
            analyticsTierType
          }
          requestDemoTitle
          sponsorPageAd {
            _id
            title
            body
            registerInterestTitle
            callToAction
            url
            adImage {
              _id
              imageProxy(input: { w: 560 })
            }
          }
        }
        ...OrganizationPageInformation_OrganizationFragment
        ...OrganizationProfileImage_OrganizationFragment
        ...MessageModal_OrganizationFragment
      }
    }
  }
  ${InfoCard.organizationFragment}
  ${OrganizationProfileImage.organizationFragment}
  ${MessageModal.organizationFragment}
`;

const FOLLOW_SPONSOR_VALID_ROUTES = {
  validRoutes: ['sponsors'],
};

export const OrganizationDetailLayout: React.FC = ({ children }) => {
  const router = useRouter();
  const { organizationId } = router.query;
  const permissions = usePermissions();
  const messageModal = useDisclosure();

  const { loading, error, data } = useQuery<
    organizationPageLayout_OrganizationQuery,
    organizationPageLayout_OrganizationQueryVariables
  >(ORGANIZATION_QUERY, {
    variables: {
      input: {
        id: organizationId.toString(),
      },
    },
  });

  const organizationPermissions = useOrganizationPermissions(
    organizationId.toString()
  );

  const showChatIcon = showChatIconInMobile();

  useAutoFollowSponsor(FOLLOW_SPONSOR_VALID_ROUTES);

  const organization = data?.organization?.result;

  return (
    <Butterfly>
      <ButterflyMain>
        <Box maxW="container.xl" mx="auto" p="4">
          <Grid templateColumns="repeat(12, 1fr)" gap="4">
            <GridItem
              colSpan={{ base: 12, lg: 3 }}
              display={{ base: 'none', lg: 'block' }}
            >
              {/* empty */}
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 8, lg: 6 }}>
              {loading || organizationPermissions.isLoading ? (
                <Loading />
              ) : error ? (
                <NoAccessComponent />
              ) : !organization ? (
                <NoAccessComponent />
              ) : (
                <Stack spacing="4">
                  <Card position="relative">
                    {organization.bannerImage?.imageProxy ? (
                      <Image
                        alt="company banner"
                        borderTopRadius="xl"
                        src={organization.bannerImage.imageProxy}
                      />
                    ) : (
                      <ImageFallback borderTopRadius="xl" aspectRatio="8/3" />
                    )}

                    {organization.currentUserOrganizationPermission
                      .canManageOrganizationProfile && (
                      <Box position="absolute" top="4" right="4">
                        <NextLink href={`/my-company/dashboard/overview`}>
                          <IconButton
                            icon={<EditIcon />}
                            aria-label="edit profile"
                            variant="outline"
                            colorScheme="gray"
                            size="sm"
                          />
                        </NextLink>
                      </Box>
                    )}

                    <Stack
                      direction={{ base: 'column', md: 'row' }}
                      alignItems={{ base: 'center' }}
                      spacing={{ base: 2, md: 4 }}
                      p="4"
                    >
                      <OrganizationProfileImage
                        mt={{ base: -16, md: -10 }}
                        h={{ base: 28, md: 36 }}
                        w={{ base: 28, md: 36 }}
                        organization={organization}
                      />
                      <Stack
                        alignItems={{ base: 'center', md: 'flex-start' }}
                        minWidth="0"
                        w="full"
                      >
                        <Heading size="md">{organization.displayName}</Heading>
                        {organization.sponsorship && (
                          <Text color="gray.600">
                            {organization.numFollowers} Followers
                          </Text>
                        )}
                        {organization.sponsorship && (
                          <Wrap
                            shouldWrapChildren
                            justify={{
                              base: 'center',
                              md: 'start',
                            }}
                          >
                            <RequestDemoButton
                              options={{
                                organizationId: organization._id,
                                registerInterestTitle:
                                  organization.sponsorship?.requestDemoTitle ||
                                  null,
                                origin: { sponsorPage: true },
                                analytics: {
                                  view: 'sponsorPage',
                                  tier:
                                    organization.sponsorship?.currentTier
                                      .analyticsTierType || '',
                                },
                              }}
                            />
                            <OrganizationFollowButton
                              organizationId={organization._id}
                            />
                            {organizationPermissions.canSubmitDirectMessageToOrganization &&
                              showChatIcon && (
                                <IconButton
                                  icon={<MessageCircleIcon color="gray.700" />}
                                  variant="outline"
                                  colorScheme="gray"
                                  aria-label="message sponsor"
                                  onClick={messageModal.onOpen}
                                ></IconButton>
                              )}
                            <MessageModal
                              isOpen={messageModal.isOpen}
                              organization={organization}
                              onClose={messageModal.onClose}
                            />
                          </Wrap>
                        )}
                      </Stack>
                    </Stack>

                    {organization.sponsorship && (
                      <>
                        <Divider />

                        <TabLinkList px="4">
                          <TabLink
                            href={`/sponsors/${organizationId}/overview`}
                          >
                            Overview
                          </TabLink>
                          <TabLink
                            href={`/sponsors/${organizationId}/discussions`}
                          >
                            Discussions
                          </TabLink>
                          <TabLink href={`/sponsors/${organizationId}/events`}>
                            Events
                          </TabLink>
                          {permissions.isAuthenticated && (
                            <TabLink
                              href={`/sponsors/${organizationId}/people`}
                            >
                              People
                            </TabLink>
                          )}
                        </TabLinkList>
                      </>
                    )}
                  </Card>

                  {children}
                </Stack>
              )}
            </GridItem>
            <GridItem
              colSpan={{ base: 12, md: 4, lg: 3 }}
              display={{ base: 'none', md: 'block' }}
            >
              {organization && (
                <Stack spacing="4">
                  <InfoCard organization={organization} />
                  {organization.sponsorship?.sponsorPageAd && (
                    <SponsorPagePromotionCard
                      title={organization.sponsorship.sponsorPageAd.title}
                      body={organization.sponsorship.sponsorPageAd.body}
                      callToAction={
                        organization.sponsorship.sponsorPageAd.callToAction
                      }
                      url={organization.sponsorship.sponsorPageAd.url}
                      image={
                        organization.sponsorship.sponsorPageAd.adImage
                          ?.imageProxy || undefined
                      }
                    />
                  )}
                  {organization.sponsorship && (
                    <TierCard
                      organizationName={organization.displayName}
                      tierName={organization.sponsorship.currentTier.name}
                    />
                  )}
                </Stack>
              )}
            </GridItem>
          </Grid>
        </Box>
      </ButterflyMain>
    </Butterfly>
  );
};
